import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { ApplicationState } from 'pages/label/store/index';
import {
    getFieldKeyFromLabel,
    getTableFieldKeyFromLabel,
    getAllDocumentLabels,
    encodeLabelString,
    uniqueByKeepFirst,
    buildRegionOrders,
    makeLabelValue,
    compareOrder,
    decodeLabelString,
    validateAssignment,
    makeError,
} from 'pages/label/utils/customModel';
import {
    Labels,
    Field,
    Definitions,
    FieldType,
    LabelValueCandidate,
    FieldFormat,
    LabelValue,
    LabelType,
    HeaderType,
    TableType,
    VisualizationHint,
    ObjectField,
    PrimitiveField,
} from 'pages/label/models/customModels';
import { CustomModelAssetService } from 'pages/label/services/assetService/customModelAssetService';
import { setDocumentPrediction } from 'pages/label/store/predictions/predictions';
import { FeatureCategory } from 'pages/label/components/imageMap/contracts';
import { AssetLabelingState, LabelRouterParams } from '../../models';

export enum FieldLocation {
    field,
    definition,
}

export type MessageDescriptorArguments = {
    [name: string]: string;
};

export interface ICustomModelError {
    name: string;
    message: string;
    messageArguments?: MessageDescriptorArguments;
}

export type CustomModelState = {
    definitions: Definitions;
    fields: Field[];
    colorForFields: Record<string, string>[];
    labels: Labels;

    orders: {
        [documentId: string]: { [orderId: string]: number };
    };
    labelValueCandidates: LabelValueCandidate[];
    labelError: ICustomModelError | null;
    hideInlineLabelMenu: boolean;
};

export const initialState: CustomModelState = {
    definitions: {},
    fields: [],
    colorForFields: [],
    labels: {},
    orders: {},
    labelValueCandidates: [],
    labelError: null,
    hideInlineLabelMenu: false,
};

export const addField = createAsyncThunk<Field, Field, { rejectValue: any }>(
    'customModel/addField',
    async (field: Field, { getState, rejectWithValue }) => {
        try {
            const { customModel } = getState() as ApplicationState;
            const { fields, definitions } = customModel;
            const assetService = new CustomModelAssetService();
            const updatedFields = fields.concat(field);
            await assetService.updateFields(updatedFields, definitions);
            return field;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const updateFieldsOrder = createAsyncThunk<Field[], Field[], { rejectValue: any }>(
    'customModel/updateFieldsOrder',
    async (updatedFields: Field[], { getState, rejectWithValue }) => {
        try {
            const { customModel } = getState() as ApplicationState;
            const { definitions } = customModel;
            const assetService = new CustomModelAssetService();
            await assetService.updateFields(updatedFields, definitions);
            return updatedFields;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const addTableField = createAsyncThunk<
    {
        fields: Field[];
        definitions: Definitions;
    },
    { fieldKey: string; tableType: TableType; headerType?: HeaderType },
    { rejectValue: any }
>(
    'customModel/addTableField',
    async (
        args: { fieldKey: string; tableType: TableType; headerType?: HeaderType },
        { getState, rejectWithValue }
    ) => {
        try {
            const { fieldKey, tableType, headerType } = args;
            const { customModel } = getState() as ApplicationState;
            const { fields, definitions } = customModel;
            const assetService = new CustomModelAssetService();

            // To create 2x2 table
            const tableCellsSquare = 2;

            const getTableFields = (headerType: HeaderType, fieldType: FieldType): PrimitiveField[] =>
                Array(tableCellsSquare)
                    .fill(null)
                    .map((_, index) => {
                        const objectName = uuidv4();
                        return {
                            fieldKey: headerType === HeaderType.column ? `COLUMN${index + 1}` : `ROW${index + 1}`,
                            fieldType: fieldType === FieldType.Object ? (objectName as FieldType) : fieldType,
                            fieldFormat: FieldFormat.NotSpecified,
                        };
                    });

            const getDynamicTableDefinitions = (objectName: string): Definitions => {
                const definitions: Definitions = {
                    [objectName]: {
                        fieldKey: objectName,
                        fieldType: FieldType.Object,
                        fieldFormat: FieldFormat.NotSpecified,
                        fields: getTableFields(HeaderType.column, FieldType.String),
                    },
                };

                return definitions;
            };

            const getFixedTableDefinitions = (tableFields: PrimitiveField[], headerType: HeaderType): Definitions => {
                const definitions: Definitions = {};
                const definitionFields = getTableFields(headerType, FieldType.String);
                tableFields.forEach((tableField) => {
                    const objectName = tableField.fieldType;

                    definitions[objectName] = {
                        fieldKey: objectName,
                        fieldType: FieldType.Object,
                        fieldFormat: FieldFormat.NotSpecified,
                        fields: definitionFields,
                    };
                });

                return definitions;
            };

            let field: any = { fieldKey, fieldFormat: FieldFormat.NotSpecified };
            let newDefinitions: Definitions = {};

            if (tableType === TableType.dynamic) {
                const objectName = uuidv4();
                field = { ...field, fieldType: FieldType.Array, itemType: objectName };
                newDefinitions = getDynamicTableDefinitions(objectName);
            } else {
                if (headerType === HeaderType.column) {
                    const tableFields = getTableFields(HeaderType.row, FieldType.Object);
                    field = {
                        ...field,
                        fieldType: FieldType.Object,
                        fields: tableFields,
                        visualizationHint: VisualizationHint.Vertical,
                    };
                    newDefinitions = getFixedTableDefinitions(tableFields, HeaderType.column);
                } else {
                    const tableFields = getTableFields(HeaderType.column, FieldType.Object);
                    field = {
                        ...field,
                        fieldType: FieldType.Object,
                        fields: tableFields,
                        visualizationHint: VisualizationHint.Horizontal,
                    };
                    newDefinitions = getFixedTableDefinitions(tableFields, HeaderType.row);
                }
            }

            const updatedFields = fields.concat(field);
            const updatedDefinitions = { ...definitions, ...newDefinitions };

            await assetService.updateFields(updatedFields, updatedDefinitions);
            return { fields: updatedFields, definitions: updatedDefinitions };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const switchSubType = createAsyncThunk<
    { index: number; field: Field },
    { fieldKey: string; fieldType: FieldType },
    { rejectValue: any }
>(
    'customModel/switchSubType',
    async (args: { fieldKey: string; fieldType: FieldType }, { getState, rejectWithValue }) => {
        try {
            const { fieldKey, fieldType } = args;
            const { customModel } = getState() as ApplicationState;
            const { fields, definitions } = customModel;
            const assetService = new CustomModelAssetService();

            // Find the target field and switch its type.
            const fieldIndex = fields.findIndex((field) => field.fieldKey === fieldKey)!;
            const updatedField = { ...fields[fieldIndex] };
            updatedField.fieldType = fieldType;

            // Update to origin fields.
            const updatedFields = fields.slice();
            updatedFields.splice(fieldIndex, 1, updatedField);
            await assetService.updateFields(updatedFields, definitions);

            return { index: fieldIndex, field: updatedField };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const switchTableFieldsSubType = createAsyncThunk<
    {
        definitions: Definitions;
    },
    { tableFieldKey: string; headerField: Field; newType: FieldType },
    { rejectValue: any }
>(
    'customModel/switchTableFieldsSubType ',
    async (args: { tableFieldKey: string; headerField: Field; newType: FieldType }, { getState, rejectWithValue }) => {
        try {
            const { tableFieldKey, headerField, newType } = args;
            const { customModel } = getState() as ApplicationState;
            const { fields, definitions } = customModel;
            const { fieldKey, fieldType } = headerField;
            if (fieldType === newType) {
                return {
                    definitions,
                };
            }

            const assetService = new CustomModelAssetService();
            const updatedDefinitions = { ...definitions };
            const originTableFieldIndex = fields.findIndex((field) => field.fieldKey === tableFieldKey);
            const originTableField: any = fields[originTableFieldIndex];
            // Only update definitions.
            const fieldDefinitionNames = originTableField.itemType
                ? [originTableField.itemType]
                : originTableField.fields.map((field) => field.fieldType);
            fieldDefinitionNames.forEach((name) => {
                const definition = { ...definitions[name] };
                const updatedFields = definition.fields.map((field) =>
                    field.fieldKey === fieldKey ? { ...field, fieldType: newType } : field
                );
                updatedDefinitions[name] = { ...definition, fields: updatedFields };
            });
            await assetService.updateFields(fields, updatedDefinitions);

            return { definitions: updatedDefinitions };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const deleteField = createAsyncThunk<
    {
        fields: Field[];
        labels: Labels;
        definitions: Definitions;
    },
    { fieldKey: string; params: LabelRouterParams },
    { rejectValue: any }
>(
    'customModel/deleteField',
    async (args: { fieldKey: string; params: LabelRouterParams }, { getState, rejectWithValue }) => {
        try {
            const { fieldKey, params } = args;
            const { customModel, documents } = getState() as ApplicationState;
            const { fields, labels, definitions } = customModel;
            const assetService = new CustomModelAssetService();

            // Fetch all document labels and update the label data with target field.
            let allLabels = await getAllDocumentLabels(labels, documents.documents, assetService, params);

            const updatedLabels = {};
            Object.entries(allLabels).forEach(([documentName, labels]) => {
                if (labels.find((label) => getFieldKeyFromLabel(label) === fieldKey)) {
                    const updatedDocLabels = labels.filter((label) => getFieldKeyFromLabel(label) !== fieldKey);
                    updatedLabels[documentName] = updatedDocLabels;
                }
            });
            allLabels = { ...allLabels, ...updatedLabels };

            // Update fields and definitions.
            const updatedFields = fields.filter((field) => field.fieldKey !== fieldKey);
            const targetField = fields.find((field) => field.fieldKey === fieldKey)! as any;
            const updatedDefinitions = { ...definitions };
            if (targetField.itemType) {
                // Delete dynamic row table cell definitions.
                delete updatedDefinitions[targetField.itemType];
            }
            if (targetField.fields) {
                // Delete fixed table cell definitions.
                targetField.fields
                    .map((field) => field.fieldType)
                    .forEach((obj) => {
                        delete updatedDefinitions[obj];
                    });
            }

            await Promise.all([
                assetService.updateFields(updatedFields, updatedDefinitions),
                assetService.updateAllDocumentLables(updatedLabels, params),
            ]);
            return { fields: updatedFields, labels: allLabels, definitions: updatedDefinitions };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const renameField = createAsyncThunk<
    {
        fields: Field[];
        labels: Labels;
    },
    { fieldKey: string; newName: string; params: LabelRouterParams },
    { rejectValue: any }
>(
    'customModel/renameField',
    async (args: { fieldKey: string; newName: string; params: LabelRouterParams }, { getState, rejectWithValue }) => {
        try {
            const { fieldKey, newName, params } = args;
            const { customModel, documents } = getState() as ApplicationState;
            const { fields, labels, definitions } = customModel;
            const assetService = new CustomModelAssetService();

            // Fetch all document labels and update the label data with target field.
            let allLabels = await getAllDocumentLabels(labels, documents.documents, assetService, params);

            // Update labels.
            const updatedLabels = {};
            Object.entries(allLabels).forEach(([documentName, labels]) => {
                // Only process the document that contains target label.
                if (labels.find((label) => getFieldKeyFromLabel(label) === fieldKey)) {
                    const updatedDocLabels = labels.map((label) => {
                        if (getFieldKeyFromLabel(label) === fieldKey) {
                            const newLabel = label.label.split('/');
                            newLabel[0] = encodeLabelString(newName); // Replace old fieldKey with new one.
                            return { ...label, label: newLabel.join('/') };
                        }
                        return label;
                    });
                    updatedLabels[documentName] = updatedDocLabels;
                }
            });
            allLabels = { ...allLabels, ...updatedLabels };

            // Update fields.
            const originFieldIndex = fields.findIndex((field) => field.fieldKey === fieldKey);
            const originField: any = fields[originFieldIndex];
            const updatedField = {
                ...originField,
                fieldKey: newName,
            };
            const updatedFields = [...fields];
            updatedFields.splice(originFieldIndex, 1, updatedField);

            // Field, SelectionMark, Signature haven't own definitions properties, no need to update definitions.

            await Promise.all([
                assetService.updateFields(updatedFields, definitions),
                assetService.updateAllDocumentLables(updatedLabels, params),
            ]);
            return { fields: updatedFields, labels: allLabels };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const deleteTableField = createAsyncThunk<
    {
        fields: Field[];
        labels: Labels;
        definitions: Definitions;
    },
    { tableFieldKey: string; fieldKey: string; fieldLocation: FieldLocation; params: LabelRouterParams },
    { rejectValue: any }
>(
    'customModel/deleteTableField',
    async (
        args: { tableFieldKey: string; fieldKey: string; fieldLocation: FieldLocation; params: LabelRouterParams },
        { getState, rejectWithValue }
    ) => {
        try {
            const { tableFieldKey, fieldKey, fieldLocation, params } = args;
            const { customModel, documents } = getState() as ApplicationState;
            const { fields, labels, definitions } = customModel;
            const assetService = new CustomModelAssetService();

            // Fetch all document labels and update the label data with target field.
            let allLabels = await getAllDocumentLabels(labels, documents.documents, assetService, params);

            // Update labels.
            const updatedLabels = {};
            const isTargetLabel = (label) =>
                getFieldKeyFromLabel(label) === tableFieldKey &&
                getTableFieldKeyFromLabel(label, fieldLocation) === fieldKey;
            Object.entries(allLabels).forEach(([documentName, labels]) => {
                // Only process the document that contains target label.
                if (labels.find(isTargetLabel)) {
                    const updatedDocLabels = labels.filter((label) => !isTargetLabel(label));
                    updatedLabels[documentName] = updatedDocLabels;
                }
            });
            allLabels = { ...allLabels, ...updatedLabels };

            const updatedFields = [...fields];
            const updatedDefinitions = { ...definitions };
            const originTableFieldIndex = fields.findIndex((field) => field.fieldKey === tableFieldKey);
            const originTableField: any = fields[originTableFieldIndex];
            if (fieldLocation === FieldLocation.field) {
                // Update fields.
                const tableFields = originTableField.fields.filter((field) => field.fieldKey !== fieldKey);
                updatedFields.splice(originTableFieldIndex, 1, { ...originTableField, fields: tableFields });

                const fieldToBeRemoved = originTableField.fields.find((field) => field.fieldKey === fieldKey);
                const definitionKeyToBeRemoved = fieldToBeRemoved.fieldType;
                delete updatedDefinitions[definitionKeyToBeRemoved];
            } else {
                // Update definitions.
                const fieldDefinitionNames = originTableField.itemType
                    ? [originTableField.itemType]
                    : originTableField.fields.map((field) => field.fieldType);

                fieldDefinitionNames.forEach((name) => {
                    const definition = { ...definitions[name] };
                    const updatedFields = definition.fields.filter((field) => field.fieldKey !== fieldKey);
                    updatedDefinitions[name] = { ...definition, fields: updatedFields };
                });
            }

            await Promise.all([
                assetService.updateFields(updatedFields, updatedDefinitions),
                assetService.updateAllDocumentLables(updatedLabels, params),
            ]);
            return { fields: updatedFields, labels: allLabels, definitions: updatedDefinitions };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const insertTableField = createAsyncThunk<
    {
        fields: Field[];
        definitions: Definitions;
    },
    { tableFieldKey: string; fieldKey: string; index: number; fieldLocation: FieldLocation },
    { rejectValue: any }
>(
    'customModel/insertTableField',
    async (
        args: { tableFieldKey: string; fieldKey: string; index: number; fieldLocation: FieldLocation },
        { getState, rejectWithValue }
    ) => {
        try {
            const { tableFieldKey, fieldKey, index, fieldLocation } = args;
            const { customModel } = getState() as ApplicationState;
            const { fields, definitions } = customModel;
            const assetService = new CustomModelAssetService();
            const updatedFields = [...fields];
            const updatedDefinitions = { ...definitions };
            const objectName = uuidv4();
            const insertField: any = {
                fieldKey,
                fieldType: fieldLocation === FieldLocation.field ? objectName : FieldType.String,
                fieldFormat: FieldFormat.NotSpecified,
            };
            const tableFieldIndex = fields.findIndex((field) => field.fieldKey === tableFieldKey);
            const tableField: any = fields[tableFieldIndex];

            if (fieldLocation === FieldLocation.field) {
                const insertedFields = (fields[tableFieldIndex] as any).fields.slice();
                insertedFields.splice(index, 0, insertField);
                const updatedTableField = { ...fields[tableFieldIndex], fields: insertedFields };
                updatedFields.splice(tableFieldIndex, 1, updatedTableField);

                const templateFieldType = tableField.fields[0].fieldType;
                const definitionFieldsTemplate = updatedDefinitions[templateFieldType];
                const insertDefinition: ObjectField = {
                    fieldKey: objectName,
                    fieldType: FieldType.Object,
                    fields: definitionFieldsTemplate.fields,
                    fieldFormat: FieldFormat.NotSpecified,
                };
                updatedDefinitions[objectName] = insertDefinition;
            } else {
                const fieldDefinitionNames = tableField.itemType
                    ? [tableField.itemType]
                    : tableField.fields.map((field) => field.fieldType);
                fieldDefinitionNames.forEach((name) => {
                    const definition = { ...definitions[name] };
                    const updatedFields = [...updatedDefinitions[name].fields];
                    updatedFields.splice(index, 0, insertField);

                    updatedDefinitions[name] = { ...definition, fields: updatedFields };
                });
            }

            await assetService.updateFields(updatedFields, updatedDefinitions);

            return { fields: updatedFields, definitions: updatedDefinitions };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const renameTableField = createAsyncThunk<
    {
        fields: Field[];
        labels: Labels;
        definitions: Definitions;
    },
    {
        tableFieldKey: string;
        fieldKey: string;
        newName: string;
        fieldLocation: FieldLocation;
        params: LabelRouterParams;
    },
    { rejectValue: any }
>(
    'customModel/renameTableField',
    async (
        args: {
            tableFieldKey: string;
            fieldKey: string;
            newName: string;
            fieldLocation: FieldLocation;
            params: LabelRouterParams;
        },
        { getState, rejectWithValue }
    ) => {
        try {
            const { tableFieldKey, fieldKey, newName, fieldLocation, params } = args;
            const { customModel, documents } = getState() as ApplicationState;
            const { fields, labels, definitions } = customModel;
            const assetService = new CustomModelAssetService();

            // Fetch all document labels and update the label data with target field.
            let allLabels = await getAllDocumentLabels(labels, documents.documents, assetService, params);

            // Update labels.
            const updatedLabels = {};
            const isTargetLabel = (label) =>
                getFieldKeyFromLabel(label) === tableFieldKey &&
                getTableFieldKeyFromLabel(label, fieldLocation) === fieldKey;
            Object.entries(allLabels).forEach(([documentName, labels]) => {
                // Only process the document that contains target label.
                if (labels.find(isTargetLabel)) {
                    const updatedDocLabels = labels.map((label) => {
                        if (isTargetLabel(label)) {
                            const newLabel = label.label.split('/');
                            const index = fieldLocation === FieldLocation.field ? 1 : 2;
                            newLabel[index] = encodeLabelString(newName); // Replace old fieldKey with new one.
                            return { ...label, label: newLabel.join('/') };
                        }
                        return label;
                    });
                    updatedLabels[documentName] = updatedDocLabels;
                }
            });
            allLabels = { ...allLabels, ...updatedLabels };

            const updatedFields = [...fields];
            const updatedDefinitions = { ...definitions };
            const originTableFieldIndex = fields.findIndex((field) => field.fieldKey === tableFieldKey);
            const originTableField: any = fields[originTableFieldIndex];
            if (fieldLocation === FieldLocation.field) {
                // Update fields.
                const tableFields = originTableField.fields.map((field) =>
                    field.fieldKey === fieldKey ? { ...field, fieldKey: newName } : field
                );
                updatedFields.splice(originTableFieldIndex, 1, { ...originTableField, fields: tableFields });
            } else {
                // Update definitions.
                const fieldDefinitionNames = originTableField.itemType
                    ? [originTableField.itemType]
                    : originTableField.fields.map((field) => field.fieldType);

                fieldDefinitionNames.forEach((name) => {
                    const definition = { ...definitions[name] };
                    const updatedFields = updatedDefinitions[name].fields.map((field) => {
                        return field.fieldKey === fieldKey ? { ...field, fieldKey: newName } : field;
                    });
                    updatedDefinitions[name] = { ...definition, fields: updatedFields };
                });
            }

            await Promise.all([
                assetService.updateFields(updatedFields, updatedDefinitions),
                assetService.updateAllDocumentLables(updatedLabels, params),
            ]);
            return { fields: updatedFields, labels: allLabels, definitions: updatedDefinitions };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const deleteLabelByField = createAsyncThunk<
    Labels,
    { fieldKey: string; params: LabelRouterParams },
    { rejectValue: any }
>('customModel/deleteLabelByField', async (args, { getState, rejectWithValue }) => {
    try {
        const { customModel, documents } = getState() as ApplicationState;
        const { labels } = customModel;
        const assetService = new CustomModelAssetService();

        const { id: fileId } = documents.currentDocument!;

        const updatedDocumentLabels = {
            [fileId!]: labels[fileId!].filter((label) => getFieldKeyFromLabel(label) !== args.fieldKey),
        };
        const updatedLabels = { ...labels, ...updatedDocumentLabels };

        await assetService.updateDocumentLabels(
            updatedLabels,
            fileId!,
            args.params,
            AssetLabelingState.ManuallyLabeled
        );

        return updatedLabels;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const assignLabel = createAsyncThunk<
    Labels,
    { labelName: string; params: LabelRouterParams },
    { rejectValue: any }
>(
    'customModel/assignLabel',
    async (args: { labelName: string; params: LabelRouterParams }, { getState, rejectWithValue }) => {
        try {
            const { customModel, documents } = getState() as ApplicationState;
            const { labels, fields, labelValueCandidates, orders } = customModel;

            if (labelValueCandidates.length === 0) {
                // No label candidate to be assigned, just return.
                return labels;
            }

            // Step 1. Remove duplicated items in LabelValueCandidates.
            const uniqueCandidates: LabelValueCandidate[] = uniqueByKeepFirst(
                labelValueCandidates,
                (item: LabelValueCandidate) => JSON.stringify(item.boundingBoxes)
            );

            // Step 2. Check invalid assignment and throw errors.
            const fieldKey = decodeLabelString(args.labelName.split('/')[0]);
            const field = fields.find((f) => f.fieldKey === fieldKey);
            validateAssignment(uniqueCandidates, field!);

            // Step 3. Check cross-page label issue and throw errors.
            const { currentPage, id: fileId } = documents.currentDocument!;

            const labelValueCandidatePageNum = labelValueCandidates[0].page;
            const currLabelValuePageNum = labels[fileId!]?.find(({ label }) => label === args.labelName)?.value[0].page;

            if (currLabelValuePageNum && labelValueCandidatePageNum !== currLabelValuePageNum) {
                const crossPageLabelError = makeError(
                    'Cross-page label error',
                    `Sorry, we don't support cross-page labeling with the same field. You have label regions with same field name <b>${args.labelName}</b> across 2 pages.`
                );
                return rejectWithValue(crossPageLabelError);
            }

            // Step 4. Remove existed label.value if it occurred in uniqueCandidates.
            const uniqCandidateBoxes = uniqueCandidates.map((candidate) => JSON.stringify(candidate.boundingBoxes));
            const documentLabels = labels[fileId!]
                ? labels[fileId!].slice().map((documentLabel) => {
                      const labelPageNum = documentLabel.value[0].page;
                      const remainingValue = documentLabel.value.filter(
                          (value) =>
                              labelValueCandidatePageNum !== labelPageNum ||
                              !uniqCandidateBoxes.includes(JSON.stringify(value.boundingBoxes))
                      );

                      if (remainingValue.length !== documentLabel.value.length) {
                          return { ...documentLabel, value: remainingValue };
                      }
                      return documentLabel;
                  })
                : [];

            // Step 5. Check if labelName existed in documentLabels.
            const iLabel = documentLabels.findIndex((docLabel) => docLabel.label === args.labelName);
            const candidatesValue: LabelValue[] = uniqueCandidates.map(makeLabelValue);
            const isSingleDrawRegion =
                uniqueCandidates.length === 1 && uniqueCandidates[0].category === FeatureCategory.DrawnRegion;
            if (iLabel === -1) {
                // Step 5.a. Add label.
                documentLabels.push({
                    label: args.labelName,
                    value: candidatesValue.sort((a, b) => compareOrder(a, b, orders[fileId!], currentPage)),
                    labelType: isSingleDrawRegion ? LabelType.Region : undefined,
                });
            } else {
                // Step 5.b. Merge or replace label.
                if (isSingleDrawRegion) {
                    documentLabels[iLabel] = {
                        ...documentLabels[iLabel],
                        value: candidatesValue,
                        labelType: LabelType.Region,
                    };
                } else if (field?.fieldType === FieldType.Signature || field?.fieldType === FieldType.SelectionMark) {
                    documentLabels[iLabel] = {
                        ...documentLabels[iLabel],
                        value: candidatesValue,
                    };
                } else if (documentLabels[iLabel].labelType === LabelType.Region) {
                    // Replace the existing region with text.
                    documentLabels[iLabel] = {
                        ...documentLabels[iLabel],
                        value: candidatesValue.sort((a, b) => compareOrder(a, b, orders[fileId!], currentPage)),
                        labelType: undefined,
                    };
                } else {
                    // Concat text.
                    documentLabels[iLabel] = {
                        ...documentLabels[iLabel],
                        value: documentLabels[iLabel].value
                            .concat(candidatesValue)
                            .sort((a, b) => compareOrder(a, b, orders[fileId!], currentPage)),
                    };
                }
            }

            // Step 6. Remove empty label.
            const updatedLabel = { [fileId!]: documentLabels.filter((label) => label.value.length > 0) };

            // Step 7: save labels.json
            // Note: for error handling purpose we await here, but for UX consideration we can call and ignore.
            const assetService = new CustomModelAssetService();
            await assetService.updateDocumentLabels(
                updatedLabel,
                fileId!,
                args.params,
                AssetLabelingState.ManuallyLabeled
            );

            return { ...labels, ...updatedLabel };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const updateLabel = createAsyncThunk<
    Labels,
    {
        labelName: string;
        oldCandidate: LabelValueCandidate;
        newCandidate: LabelValueCandidate;
        params: LabelRouterParams;
    },
    { rejectValue: any }
>(
    'customModel/updateLabel',
    async (
        args: {
            labelName: string;
            oldCandidate: LabelValueCandidate;
            newCandidate: LabelValueCandidate;
            params: LabelRouterParams;
        },
        { getState, rejectWithValue }
    ) => {
        try {
            const { labelName, oldCandidate, newCandidate, params } = args;
            const { customModel, documents } = getState() as ApplicationState;
            const { labels } = customModel;

            const { name: documentId, id: fileId } = documents.currentDocument!;

            // Find Label
            const iLabel = labels[documentId].findIndex((label) => label.label === labelName);
            if (iLabel === -1) {
                return labels;
            }

            const updatedDocumentLabels = {
                [documentId]: labels[documentId].map((label, index) => {
                    if (index === iLabel) {
                        const updatedLabelValue = label.value.map((value) => {
                            if (JSON.stringify(value.boundingBoxes) === JSON.stringify(oldCandidate.boundingBoxes)) {
                                return { ...value, boundingBoxes: newCandidate.boundingBoxes };
                            }
                            return value;
                        });
                        return { ...label, value: updatedLabelValue };
                    }
                    return label;
                }),
            };

            // Save labels.json
            // Note: for error handling purpose we await here, but for UX consideration we can call and ignore.
            const assetService = new CustomModelAssetService();
            await assetService.updateDocumentLabels(
                updatedDocumentLabels,
                fileId!,
                params,
                AssetLabelingState.ManuallyLabeled
            );

            return { ...labels, ...updatedDocumentLabels };
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const deleteLabelByLabel = createAsyncThunk<
    Labels,
    { targetLabel: string; params: LabelRouterParams },
    { rejectValue: any }
>('customModel/deleteLabelByLabel', async (args, { getState, rejectWithValue }) => {
    try {
        const { customModel, documents } = getState() as ApplicationState;
        const { labels } = customModel;
        const assetService = new CustomModelAssetService();

        const { id: fileId } = documents.currentDocument!;

        const updatedDocumentLabels = {
            [fileId!]: labels[fileId!].filter((label) => label.label !== args?.targetLabel),
        };
        const updatedLabels = { ...labels, ...updatedDocumentLabels };

        await assetService.updateDocumentLabels(
            updatedDocumentLabels,
            fileId!,
            args?.params,
            AssetLabelingState.ManuallyLabeled
        );

        return updatedLabels;
    } catch (err) {
        return rejectWithValue(err);
    }
});

const customModel = createSlice({
    name: 'customModel',
    initialState,
    reducers: {
        setHideInlineLabelMenu(state, action) {
            state.hideInlineLabelMenu = action.payload;
        },
        setDefinitions(state, action) {
            state.definitions = action.payload;
        },
        setFields(state, action) {
            state.fields = action.payload;
        },
        setColorForFields(state, action) {
            state.colorForFields = action.payload;
        },
        setColorForFieldsByName(state, action) {
            const getDynamicKey = (obj: Record<string, string>) => Object.keys(obj)[0];
            const { fieldName, newFieldName } = action.payload;
            const colorForFieldsCopy = [...state.colorForFields];
            const originalFieldIndex = colorForFieldsCopy.findIndex(
                (colorMap) => getDynamicKey(colorMap) === fieldName
            );
            const originalFieldColor = colorForFieldsCopy[originalFieldIndex][fieldName];
            colorForFieldsCopy.splice(originalFieldIndex, 1, {
                [newFieldName]: originalFieldColor,
            });
            state.colorForFields = colorForFieldsCopy;
        },
        setLabelsById(state, action) {
            const { id, labels } = action.payload;
            state.labels[id] = labels;
        },
        setLabelValueCandidates(state, action) {
            state.labelValueCandidates = action.payload;
        },
        deleteLabelByName(state, action) {
            const labelNameToDelete = action.payload;
            delete state.labels[labelNameToDelete];
        },
        clearLabelError(state) {
            state.labelError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(addField.fulfilled, (state, action) => {
                state.fields.push(action.payload);
            })
            .addCase(updateFieldsOrder.fulfilled, (state, action) => {
                state.fields = action.payload;
            })
            .addCase(switchSubType.fulfilled, (state, action) => {
                const { index, field } = action.payload;
                state.fields[index] = field;
            })
            .addCase(switchTableFieldsSubType.fulfilled, (state, action) => {
                const { definitions } = action.payload;
                state.definitions = definitions;
            })
            .addCase(setDocumentPrediction, (state, action) => {
                // Calculate bounding box orders for later sorting usage.
                const { id, analyzeResponse } = action.payload;
                state.orders[id] = buildRegionOrders(analyzeResponse.analyzeResult);
            })
            .addCase(renameField.fulfilled, (state, action) => {
                const { fields, labels } = action.payload;
                state.fields = fields;
                state.labels = labels;
            })
            .addMatcher(isAnyOf(insertTableField.fulfilled, addTableField.fulfilled), (state, action) => {
                const { fields, definitions } = action.payload;
                state.fields = fields;
                state.definitions = definitions;
            })
            .addMatcher(
                isAnyOf(
                    assignLabel.fulfilled,
                    updateLabel.fulfilled,
                    deleteLabelByField.fulfilled,
                    deleteLabelByLabel.fulfilled
                ),
                (state, action) => {
                    state.labels = action.payload;
                }
            )
            .addMatcher(
                isAnyOf(renameTableField.fulfilled, deleteField.fulfilled, deleteTableField.fulfilled),
                (state, action) => {
                    const { fields, labels, definitions } = action.payload;
                    state.fields = fields;
                    state.labels = labels;
                    state.definitions = definitions;
                }
            )
            .addMatcher(
                isAnyOf(
                    addField.rejected,
                    updateFieldsOrder.rejected,
                    addTableField.rejected,
                    switchSubType.rejected,
                    switchTableFieldsSubType.rejected,
                    deleteField.rejected,
                    deleteTableField.rejected,
                    renameField.rejected,
                    renameTableField.rejected,
                    deleteLabelByField.rejected,
                    deleteLabelByLabel.rejected,
                    insertTableField.rejected,
                    assignLabel.rejected,
                    updateLabel.rejected
                ),
                (state, action) => {
                    state.labelError = action.payload;
                }
            );
    },
});

export const {
    setHideInlineLabelMenu,
    setDefinitions,
    setFields,
    setColorForFields,
    setColorForFieldsByName,
    setLabelsById,
    setLabelValueCandidates,
    deleteLabelByName,
    clearLabelError,
} = customModel.actions;
export const reducer = customModel.reducer;
